<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import {
  useKnowledgeBase,
  type KnowledgeBaseEntry,
} from '~/composables/useKnowledgeBase'
import KnowledgeBaseEntryEditor from './KnowledgeBaseEntryEditor.vue'
import KnowledgeBaseEntryView from './KnowledgeBaseEntryView.vue'
import { useRoute } from 'vue-router'

const props = defineProps<{
  fetchAllEntries?: boolean
}>()

const knowledgeBase = useKnowledgeBase()
const activeTab = ref<'list' | 'view' | 'edit'>('list')
const route = useRoute()

// Request form state
const showRequestForm = ref(false)
const requestContent = ref('')
const isSubmittingRequest = ref(false)
const requestSubmitted = ref(false)

// Wrapper computed properties to properly type the values
const isModalOpen = computed(() => knowledgeBase.isModalOpen.value)
const hasEditPermissions = computed(
  () => knowledgeBase.hasEditPermissions.value,
)
const isEditing = computed(() => knowledgeBase.isEditing.value)
const matchingEntries = computed(() => knowledgeBase.matchingEntries.value)
const currentEntryForEditing = computed(
  () => knowledgeBase.currentEntryForEditing.value,
)
const selectedEntryId = computed(() => knowledgeBase.selectedEntryId.value)
const selectedEntry = computed(() => knowledgeBase.selectedEntry.value)

// Fetch entries based on prop when the component is mounted
onMounted(() => {
  if (props.fetchAllEntries) {
    knowledgeBase.fetchAllEntries()
  }
})

// Watch for modal open state changes to fetch entries
watch(
  () => knowledgeBase.isModalOpen.value,
  async (isOpen) => {
    if (isOpen && !props.fetchAllEntries) {
      // Get the current path from the route when the modal opens
      const currentPath = route.path
      // Ensure the path starts with a forward slash and remove any trailing slash
      const formattedPath = currentPath.startsWith('/')
        ? currentPath
        : `/${currentPath}`
      await knowledgeBase.fetchEntriesByPath(formattedPath)
    }
  },
)

// Add a watcher for loading state
watch(
  () => knowledgeBase.isLoading.value,
  (isLoading) => {
    // Keep this watcher for loading state management
  },
)

// When entries change or when entering edit mode, update tab
watch(
  () => knowledgeBase.isEditing.value,
  (isEditing) => {
    activeTab.value = isEditing ? 'edit' : 'list'
  },
)

// Watch for selection changes
watch(
  () => knowledgeBase.selectedEntryId.value,
  (newValue) => {
    if (newValue) {
      activeTab.value = 'view'
    } else {
      activeTab.value = 'list'
    }
  },
)

function closeModal() {
  knowledgeBase.closeModal()
}

function handleSave(entry: KnowledgeBaseEntry) {
  knowledgeBase.saveEntry(entry)
  activeTab.value = 'list'

  // Refresh entries if showing all entries
  if (props.fetchAllEntries) {
    knowledgeBase.fetchAllEntries()
  }
}

function handleCancel() {
  knowledgeBase.isEditing.value = false
  activeTab.value = selectedEntryId.value ? 'view' : 'list'
}

function handleCreate() {
  knowledgeBase.createNewEntry()
  activeTab.value = 'edit'
}

function handleEdit(entry: KnowledgeBaseEntry) {
  knowledgeBase.startEditing(entry)
  activeTab.value = 'edit'
}

function handleDelete(id: string) {
  knowledgeBase.deleteEntry(id)

  // Refresh entries if showing all entries
  if (props.fetchAllEntries) {
    knowledgeBase.fetchAllEntries()
  }
}

function handleSelectEntry(id: string) {
  knowledgeBase.selectEntry(id)
}

function handleBackToList() {
  knowledgeBase.selectedEntryId.value = null
  activeTab.value = 'list'
}

function showRequestFormHandler() {
  showRequestForm.value = true
  requestSubmitted.value = false
}

function cancelRequest() {
  showRequestForm.value = false
  requestContent.value = ''
  requestSubmitted.value = false
}

async function submitRequest() {
  if (!requestContent.value.trim()) {
    return
  }

  isSubmittingRequest.value = true

  try {
    const success = await knowledgeBase.requestEntry(
      requestContent.value,
      route.path,
    )

    if (success) {
      requestSubmitted.value = true
      requestContent.value = ''
      // Show success message for a few seconds then hide form
      setTimeout(() => {
        showRequestForm.value = false
        requestSubmitted.value = false
      }, 3000)
    } else {
      console.error('Failed to submit knowledge base request')
    }
  } catch (error) {
    console.error('Error submitting knowledge base request:', error)
  } finally {
    isSubmittingRequest.value = false
  }
}
</script>

<template>
  <Transition
    enter-active-class="transform transition ease-in-out duration-300"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transform transition ease-in-out duration-300"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <div
      v-if="isModalOpen"
      class="fixed top-0 right-0 h-screen bg-white shadow-2xl z-40 w-96 flex flex-col border-l border-gray-200 overflow-hidden knowledge-base-sidebar"
      style="transform: none"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-gray-200 flex justify-between items-center"
      >
        <div class="flex items-center space-x-4">
          <h2 class="text-xl font-semibold text-gray-800">Knowledge Base</h2>
          <nuxt-link
            to="/knowledge-base"
            class="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            <div
              class="i-heroicons-solid-arrow-top-right-on-square h-4 w-4 mr-1"
            />
            Show all entries
          </nuxt-link>
        </div>
        <button
          class="text-gray-400 hover:text-gray-500"
          type="button"
          @click="closeModal"
        >
          <span class="sr-only">Close</span>
          <div aria-hidden="true" class="i-heroicons-outline-x h-6 w-6" />
        </button>
      </div>

      <!-- Navigation tabs when not in list view -->
      <div v-if="activeTab !== 'list'" class="flex border-b border-gray-200">
        <button
          class="px-4 py-2 font-medium text-sm flex items-center text-gray-500 hover:text-gray-700"
          @click="handleBackToList"
        >
          <div class="i-heroicons-solid-arrow-left mr-1 h-4 w-4" />
          Back to List
        </button>
        <button
          v-if="hasEditPermissions && selectedEntry && activeTab === 'view'"
          class="px-4 py-2 font-medium text-sm ml-auto text-primary-600 hover:text-primary-700"
          @click="handleEdit(selectedEntry)"
        >
          <div class="i-heroicons-solid-pencil h-4 w-4 inline mr-1" />
          Edit
        </button>
      </div>

      <!-- Content area -->
      <div class="flex-1 min-h-0 overflow-auto">
        <!-- List View -->
        <div v-if="activeTab === 'list'" class="p-6">
          <div v-if="knowledgeBase.isLoading.value" class="text-center py-8">
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"
            ></div>
          </div>
          <div
            v-else-if="matchingEntries.length === 0"
            class="text-center py-8"
          >
            <div
              class="i-icon-park-outline:empty text-gray-400 w-16 h-16 mx-auto mb-4"
            />
            <h3 class="text-lg font-medium text-gray-900">
              No knowledge base entries
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              There are no knowledge base entries for this page yet.
            </p>

            <!-- Admin actions -->
            <div v-if="hasEditPermissions" class="mt-6">
              <button
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                type="button"
                @click="handleCreate"
              >
                Create New Entry
              </button>
            </div>

            <!-- Request form (available for all users for testing) -->
            <div class="mt-6">
              <div v-if="!showRequestForm && !requestSubmitted">
                <button
                  class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  type="button"
                  @click="showRequestFormHandler"
                >
                  <div class="i-heroicons-solid-plus h-4 w-4 mr-2" />
                  Request Knowledge Base Entry
                </button>
              </div>

              <!-- Request form -->
              <div
                v-else-if="showRequestForm && !requestSubmitted"
                class="mt-4 text-left"
              >
                <h4 class="text-sm font-medium text-gray-900 mb-2">
                  Request Knowledge Base Entry
                </h4>
                <p class="text-xs text-gray-500 mb-3">
                  Describe what information you're looking for on this page ({{
                    route.path
                  }})
                </p>
                <textarea
                  v-model="requestContent"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                  rows="4"
                  placeholder="What information are you missing on this page?"
                  :disabled="isSubmittingRequest"
                ></textarea>
                <div class="mt-3 flex justify-end space-x-2">
                  <button
                    class="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    type="button"
                    @click="cancelRequest"
                    :disabled="isSubmittingRequest"
                  >
                    Cancel
                  </button>
                  <button
                    class="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    type="button"
                    @click="submitRequest"
                    :disabled="isSubmittingRequest || !requestContent.trim()"
                  >
                    <span v-if="isSubmittingRequest">Submitting...</span>
                    <span v-else>Submit Request</span>
                  </button>
                </div>
              </div>

              <!-- Success message -->
              <div
                v-else-if="requestSubmitted"
                class="mt-4 p-4 bg-green-50 border border-green-200 rounded-md"
              >
                <p class="text-sm font-medium text-green-800">
                  Request submitted successfully!
                </p>
                <p class="mt-1 text-sm text-green-700">
                  Your request has been sent to the administrators. They will
                  review it and add the appropriate knowledge base entry.
                </p>
              </div>
            </div>
          </div>

          <div v-else>
            <div v-if="hasEditPermissions" class="flex justify-end mb-4">
              <button
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                type="button"
                @click="handleCreate"
              >
                Create New Entry
              </button>
            </div>

            <div class="space-y-2">
              <div
                v-for="entry in matchingEntries"
                :key="entry.id"
                class="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                @click="handleSelectEntry(entry.id)"
              >
                <h3 class="font-medium text-gray-900">{{ entry.title }}</h3>
                <p class="text-sm text-gray-500 mt-1">
                  Last updated:
                  {{ new Date(entry.updatedAt).toLocaleDateString() }}
                </p>
                <div class="flex mt-2">
                  <button
                    class="text-sm text-primary-600 hover:text-primary-800 flex items-center"
                    @click.stop="handleSelectEntry(entry.id)"
                  >
                    <div class="i-heroicons-solid-eye h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    v-if="hasEditPermissions"
                    class="text-sm text-gray-600 hover:text-gray-800 flex items-center ml-4"
                    @click.stop="handleEdit(entry)"
                  >
                    <div class="i-heroicons-solid-pencil h-4 w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    v-if="hasEditPermissions"
                    class="text-sm text-red-600 hover:text-red-800 flex items-center ml-4"
                    @click.stop="handleDelete(entry.id)"
                  >
                    <div class="i-heroicons-solid-trash h-4 w-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- View Entry Mode -->
        <div v-else-if="activeTab === 'view' && selectedEntry" class="p-6">
          <KnowledgeBaseEntryView
            :entry="selectedEntry"
            @edit="handleEdit"
            @delete="handleDelete"
          />
        </div>

        <!-- Edit Mode -->
        <div
          v-else-if="activeTab === 'edit' && currentEntryForEditing"
          class="p-6"
        >
          <KnowledgeBaseEntryEditor
            :entry="currentEntryForEditing"
            @save="handleSave"
            @cancel="handleCancel"
          />
        </div>
      </div>
    </div>
  </Transition>

  <!-- Overlay for mobile only -->
  <Transition
    enter-active-class="transition-opacity ease-linear duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity ease-linear duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isModalOpen"
      class="fixed inset-0 bg-gray-500 bg-opacity-25 z-30 lg:hidden"
      @click="closeModal"
    ></div>
  </Transition>
</template>

<style scoped>
/* Make sure the sidebar stays at the right width */
.knowledge-base-sidebar {
  width: 24rem; /* 384px */
}

@media (max-width: 1023px) {
  .knowledge-base-sidebar {
    width: 100%;
    max-width: 24rem;
  }
}

/* Existing styles for content rendering */
.kb-entry :deep(h1) {
  @apply text-xl font-bold mt-6 mb-2 text-gray-900;
}

.kb-entry :deep(h2) {
  @apply text-lg font-semibold mt-5 mb-2 text-gray-800;
}

.kb-entry :deep(h3) {
  @apply text-base font-medium mt-4 mb-2 text-gray-800;
}

.kb-entry :deep(p) {
  @apply mb-3 text-gray-700;
}

.kb-entry :deep(ul) {
  @apply list-disc pl-6 mb-3;
}

.kb-entry :deep(ol) {
  @apply list-decimal pl-6 mb-3;
}

.kb-entry :deep(li) {
  @apply mb-1;
}

.kb-entry :deep(strong) {
  @apply font-semibold;
}

.kb-entry :deep(em) {
  @apply italic;
}

.kb-entry :deep(a) {
  @apply text-primary-600 hover:text-primary-800 hover:underline;
}

.kb-entry :deep(code) {
  @apply bg-gray-100 text-red-500 px-1.5 py-0.5 rounded font-mono text-sm;
}

.kb-entry :deep(pre) {
  @apply bg-gray-100 p-3 rounded-md overflow-auto my-3;
}

.kb-entry :deep(blockquote) {
  @apply border-l-4 border-gray-200 pl-4 italic text-gray-600 my-3;
}

.kb-entry :deep(hr) {
  @apply my-6 border-t border-gray-200;
}

.kb-entry :deep(table) {
  @apply min-w-full divide-y divide-gray-200 my-3;
}

.kb-entry :deep(th) {
  @apply px-3 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.kb-entry :deep(td) {
  @apply px-3 py-2 whitespace-nowrap text-sm text-gray-500;
}
</style>
